const usd0 = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });
const int = new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 });

export function money(n: number | null | undefined): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return "—";
  return usd0.format(n);
}

/** $1.2M, $845K, $9,120 — compact but never lossy below $10K. */
export function moneyShort(n: number | null | undefined): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return "—";
  const a = Math.abs(n);
  const sign = n < 0 ? "-" : "";
  // Thresholds sit just under each unit so $999,600 reads "$1M", not "$1000K".
  if (a >= 999_500_000) return `${sign}$${trim(a / 1e9, a >= 9.995e9 ? 1 : 2)}B`;
  if (a >= 999_500) return `${sign}$${trim(a / 1e6, a >= 9_995_000 ? 1 : 2)}M`;
  if (a >= 1e4) return `${sign}$${trim(a / 1e3, a >= 99_950 ? 0 : 1)}K`;
  return usd0.format(n);
}

function trim(v: number, digits: number): string {
  return v.toFixed(digits).replace(/\.0+$/, "").replace(/(\.\d*?)0+$/, "$1");
}

export function pct(n: number | null | undefined, digits = 0): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return "—";
  return `${(n * 100).toFixed(digits)}%`;
}

export function signedPct(n: number | null | undefined, digits = 1): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return "—";
  const v = n * 100;
  return `${v > 0 ? "+" : v < 0 ? "−" : ""}${Math.abs(v).toFixed(digits)}%`;
}

export function number(n: number | null | undefined): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return "—";
  return int.format(n);
}

export function formatMetric(n: number | null | undefined, format: string, short = false): string {
  if (format === "currency") return short ? moneyShort(n) : money(n);
  if (format === "percent") return pct(n, 1);
  return number(n);
}

export function fmtDate(iso: string | null | undefined, opts: Intl.DateTimeFormatOptions = { month: "short", day: "numeric", year: "numeric" }): string {
  if (!iso) return "—";
  const d = new Date(iso.length === 10 ? iso + "T00:00:00Z" : iso);
  return d.toLocaleDateString("en-US", { ...opts, timeZone: iso.length === 10 ? "UTC" : undefined });
}

export function timeAgo(iso: string | null | undefined): string {
  if (!iso) return "never";
  const s = Math.max(0, (Date.now() - Date.parse(iso)) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)} min ago`;
  if (s < 86400) return `${Math.floor(s / 3600)} hr ago`;
  return `${Math.floor(s / 86400)} d ago`;
}

export function initials(name: string): string {
  return name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]!.toUpperCase()).join("");
}

/** Pace → status. ≥100% on/above target, 92–100% close, below that behind. */
export function paceStatus(p: number | null | undefined): "good" | "warn" | "bad" | "neutral" {
  if (p === null || p === undefined || !Number.isFinite(p)) return "neutral";
  if (p >= 1) return "good";
  if (p >= 0.92) return "warn";
  return "bad";
}
